import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import "./Labyrinthe.css";

export default function Labyrinthe() {
  const canvasRef = useRef(null);
  const minimapRef = useRef(null);
  const startRef = useRef(null);
  const messageRef = useRef(null);
  const minimapContainerRef = useRef(null);
  const moveRef = useRef({ x: 0, y: 0 });
  const lookRef = useRef({ x: 0, y: 0 });
  const joystickRadius = 48;

  const handleMoveStart = (event) => {
    const target = event.currentTarget;
    target.setPointerCapture(event.pointerId);
    target.dataset.active = "1";
    target.dataset.startX = event.clientX;
    target.dataset.startY = event.clientY;
    moveRef.current = { x: 0, y: 0 };
  };

  const handleMove = (event) => {
    const target = event.currentTarget;
    if (target.dataset.active !== "1") return;
    const startX = Number(target.dataset.startX || 0);
    const startY = Number(target.dataset.startY || 0);
    const dx = event.clientX - startX;
    const dy = event.clientY - startY;
    const dist = Math.min(Math.hypot(dx, dy), joystickRadius);
    const angle = Math.atan2(dy, dx);
    const nx = (Math.cos(angle) * dist) / joystickRadius;
    const ny = (Math.sin(angle) * dist) / joystickRadius;
    moveRef.current = { x: nx, y: -ny };
    target.style.setProperty("--stick-x", `${nx * 24}px`);
    target.style.setProperty("--stick-y", `${-ny * 24}px`);
  };

  const handleMoveEnd = (event) => {
    const target = event.currentTarget;
    target.dataset.active = "0";
    moveRef.current = { x: 0, y: 0 };
    target.style.setProperty("--stick-x", "0px");
    target.style.setProperty("--stick-y", "0px");
    target.releasePointerCapture(event.pointerId);
  };

  const handleLookStart = (event) => {
    const target = event.currentTarget;
    target.setPointerCapture(event.pointerId);
    target.dataset.active = "1";
    target.dataset.startX = event.clientX;
    target.dataset.startY = event.clientY;
    lookRef.current = { x: 0, y: 0 };
  };

  const handleLook = (event) => {
    const target = event.currentTarget;
    if (target.dataset.active !== "1") return;
    const startX = Number(target.dataset.startX || 0);
    const startY = Number(target.dataset.startY || 0);
    const dx = event.clientX - startX;
    const dy = event.clientY - startY;
    const dist = Math.min(Math.hypot(dx, dy), joystickRadius);
    const angle = Math.atan2(dy, dx);
    const nx = (Math.cos(angle) * dist) / joystickRadius;
    const ny = (Math.sin(angle) * dist) / joystickRadius;
    lookRef.current = { x: nx, y: ny };
    target.style.setProperty("--stick-x", `${nx * 24}px`);
    target.style.setProperty("--stick-y", `${ny * 24}px`);
  };

  const handleLookEnd = (event) => {
    const target = event.currentTarget;
    target.dataset.active = "0";
    lookRef.current = { x: 0, y: 0 };
    target.style.setProperty("--stick-x", "0px");
    target.style.setProperty("--stick-y", "0px");
    target.releasePointerCapture(event.pointerId);
  };

  useEffect(() => {
    let cancelled = false;
    let cleanup = () => {};

    async function setup() {
      let mazeData = null;
      try {
        const response = await fetch("/labyrinthe.json");
        if (!response.ok) throw new Error("labyrinthe.json introuvable");
        mazeData = await response.json();
      } catch {
        return;
      }
      if (cancelled) return;

      const container = canvasRef.current;
      const minimapCanvas = minimapRef.current;
      const minimapCtx = minimapCanvas?.getContext("2d");
      const startScreen = startRef.current;
      const message = messageRef.current;
      const minimapContainer = minimapContainerRef.current;
      if (!container || !minimapCanvas || !minimapCtx || !startScreen || !message || !minimapContainer) {
        return;
      }

      const rows = Number(mazeData?.rows) || 21;
      const cols = Number(mazeData?.cols) || 21;
      const CELL_SIZE = 4;
      const WALL_HEIGHT = 4;
      const WALL_THICKNESS = 0.6;
      const PLAYER_SPEED = 15.0;
      const PLAYER_HEIGHT = 1.8;
      const PLAYER_RADIUS = 0.8;

      let camera;
      let scene;
      let renderer;
      let moveForward = false;
      let moveBackward = false;
      let turnLeft = false;
      let turnRight = false;
      let canJump = false;
      let prevTime = performance.now();
      const velocity = new THREE.Vector3();
      const direction = new THREE.Vector3();
      const walls = [];
      let isGameActive = false;
      let exitPosition = null;
      let animationId = 0;
      let yaw = 0;
      let pitch = 0;

      function buildMaze3D() {
        const verticalGeometry = new THREE.BoxGeometry(WALL_THICKNESS, WALL_HEIGHT, CELL_SIZE);
        const horizontalGeometry = new THREE.BoxGeometry(CELL_SIZE, WALL_HEIGHT, WALL_THICKNESS);
        const wallMaterial = new THREE.MeshLambertMaterial({ color: 0x4444aa });

        const floorGeometry = new THREE.PlaneGeometry(cols * CELL_SIZE, rows * CELL_SIZE);
        const floorMaterial = new THREE.MeshLambertMaterial({ color: 0x222222 });
        const floor = new THREE.Mesh(floorGeometry, floorMaterial);
        floor.rotation.x = -Math.PI / 2;
        scene.add(floor);

        const verticalWalls = Array.isArray(mazeData?.verticalWalls) ? mazeData.verticalWalls : [];
        const horizontalWalls = Array.isArray(mazeData?.horizontalWalls) ? mazeData.horizontalWalls : [];
        for (let r = 0; r < rows; r += 1) {
          const row = Array.isArray(verticalWalls[r]) ? verticalWalls[r] : [];
          for (let c = 0; c <= cols; c += 1) {
            if (row[c]) {
              const wall = new THREE.Mesh(verticalGeometry, wallMaterial);
              wall.position.set(
                (c - cols / 2 - 0.5) * CELL_SIZE,
                WALL_HEIGHT / 2,
                (r - rows / 2) * CELL_SIZE
              );
              scene.add(wall);
              walls.push({ mesh: wall, halfX: WALL_THICKNESS / 2, halfZ: CELL_SIZE / 2 });
            }
          }
        }

        for (let r = 0; r <= rows; r += 1) {
          const row = Array.isArray(horizontalWalls[r]) ? horizontalWalls[r] : [];
          for (let c = 0; c < cols; c += 1) {
            if (row[c]) {
              const wall = new THREE.Mesh(horizontalGeometry, wallMaterial);
              wall.position.set(
                (c - cols / 2) * CELL_SIZE,
                WALL_HEIGHT / 2,
                (r - rows / 2 - 0.5) * CELL_SIZE
              );
              scene.add(wall);
              walls.push({ mesh: wall, halfX: CELL_SIZE / 2, halfZ: WALL_THICKNESS / 2 });
            }
          }
        }

        const endRow = Number(mazeData?.end?.row) || rows - 1;
        const endCol = Number(mazeData?.end?.col) || cols - 1;
        exitPosition = new THREE.Vector3(
          (endCol - cols / 2) * CELL_SIZE,
          0,
          (endRow - rows / 2) * CELL_SIZE
        );

        const exitGeometry = new THREE.CylinderGeometry(0.5, 0.5, 3, 8);
        const exitMaterial = new THREE.MeshLambertMaterial({ color: 0x00ff00 });
        const exit = new THREE.Mesh(exitGeometry, exitMaterial);
        exit.position.copy(exitPosition);
        exit.position.y = 1.5;
        scene.add(exit);

        const startRow = Number(mazeData?.start?.row) || 0;
        const startCol = Number(mazeData?.start?.col) || 0;
        camera.position.set(
          (startCol - cols / 2) * CELL_SIZE,
          PLAYER_HEIGHT,
          (startRow - rows / 2) * CELL_SIZE
        );
      }

      function drawMinimap() {
        minimapCtx.clearRect(0, 0, minimapCanvas.width, minimapCanvas.height);
        const cellSizeX = minimapCanvas.width / cols;
        const cellSizeY = minimapCanvas.height / rows;

        minimapCtx.fillStyle = "#222";
        minimapCtx.fillRect(0, 0, minimapCanvas.width, minimapCanvas.height);
        minimapCtx.strokeStyle = "#555";
        minimapCtx.lineWidth = 1;

        const verticalWalls = Array.isArray(mazeData?.verticalWalls) ? mazeData.verticalWalls : [];
        const horizontalWalls = Array.isArray(mazeData?.horizontalWalls) ? mazeData.horizontalWalls : [];

        minimapCtx.beginPath();
        for (let r = 0; r < rows; r += 1) {
          const row = Array.isArray(verticalWalls[r]) ? verticalWalls[r] : [];
          for (let c = 0; c <= cols; c += 1) {
            if (row[c]) {
              const x = c * cellSizeX;
              const y = r * cellSizeY;
              minimapCtx.moveTo(x, y);
              minimapCtx.lineTo(x, y + cellSizeY);
            }
          }
        }

        for (let r = 0; r <= rows; r += 1) {
          const row = Array.isArray(horizontalWalls[r]) ? horizontalWalls[r] : [];
          for (let c = 0; c < cols; c += 1) {
            if (row[c]) {
              const x = c * cellSizeX;
              const y = r * cellSizeY;
              minimapCtx.moveTo(x, y);
              minimapCtx.lineTo(x + cellSizeX, y);
            }
          }
        }
        minimapCtx.stroke();

        const endRow = Number(mazeData?.end?.row) || rows - 1;
        const endCol = Number(mazeData?.end?.col) || cols - 1;
        minimapCtx.fillStyle = "#0f0";
        minimapCtx.fillRect(endCol * cellSizeX, endRow * cellSizeY, cellSizeX, cellSizeY);
      }

      function checkCollision(position) {
        for (const wall of walls) {
          const dx = Math.abs(position.x - wall.mesh.position.x);
          const dz = Math.abs(position.z - wall.mesh.position.z);
          if (dx < wall.halfX + PLAYER_RADIUS && dz < wall.halfZ + PLAYER_RADIUS) {
            return true;
          }
        }
        return false;
      }

      function updatePlayer() {
        const time = performance.now();
        const delta = (time - prevTime) / 1000;

        velocity.y -= 9.8 * 5.0 * delta;

        const keyForward = Number(moveForward) - Number(moveBackward);
        const forward = new THREE.Vector3(0, 0, -1).applyAxisAngle(new THREE.Vector3(0, 1, 0), yaw);
        const right = new THREE.Vector3(1, 0, 0).applyAxisAngle(new THREE.Vector3(0, 1, 0), yaw);

        const moveX = (moveRef.current.x || 0) * PLAYER_SPEED * delta;
        const moveZ = ((moveRef.current.y || 0) + keyForward) * PLAYER_SPEED * delta;

        const nextPosition = camera.position.clone();
        nextPosition.addScaledVector(right, moveX);
        nextPosition.addScaledVector(forward, moveZ);

        if (!checkCollision(nextPosition)) {
          camera.position.x = nextPosition.x;
          camera.position.z = nextPosition.z;
        }

        camera.position.y += velocity.y * delta;
        if (camera.position.y < PLAYER_HEIGHT) {
          velocity.y = 0;
          camera.position.y = PLAYER_HEIGHT;
          canJump = true;
        }

        const turnDelta = (Number(turnRight) - Number(turnLeft)) * 2.2 * delta;
        yaw -= (lookRef.current.x || 0) * 0.03;
        yaw -= turnDelta;
        pitch -= (lookRef.current.y || 0) * 0.03;
        const limit = Math.PI / 2.4;
        pitch = Math.max(-limit, Math.min(limit, pitch));
        camera.rotation.y = yaw;
        camera.rotation.x = pitch;

        if (exitPosition && camera.position.distanceTo(exitPosition) < 2) {
          message.style.display = "block";
          message.textContent = "Sortie trouvée !";
          isGameActive = false;
        }

        prevTime = time;
    }

    function animate() {
      animationId = requestAnimationFrame(animate);
      if (isGameActive) updatePlayer();
      renderer.render(scene, camera);
    }

    function onWindowResize() {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    }

    function onKeyDown(event) {
      switch (event.code) {
        case "ArrowUp":
          moveForward = true;
          break;
        case "ArrowDown":
          moveBackward = true;
          break;
        case "ArrowLeft":
          turnLeft = true;
          break;
        case "ArrowRight":
          turnRight = true;
          break;
        case "Space":
          if (canJump) velocity.y += 8;
          canJump = false;
          break;
        default:
          break;
      }
    }

    function onKeyUp(event) {
      switch (event.code) {
        case "ArrowUp":
          moveForward = false;
          break;
        case "ArrowDown":
          moveBackward = false;
          break;
        case "ArrowLeft":
          turnLeft = false;
          break;
        case "ArrowRight":
          turnRight = false;
          break;
        default:
          break;
      }
    }

    function onStart() {
      isGameActive = true;
      startScreen.style.display = "none";
      minimapContainer.style.display = "block";
    }

    scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1a1a1a);
    scene.fog = new THREE.Fog(0x1a1a1a, 0, 40);

    camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);

    const light = new THREE.HemisphereLight(0xeeeeff, 0x777788, 0.75);
    light.position.set(0.5, 1, 0.75);
    scene.add(light);

    const torch = new THREE.PointLight(0xffaa00, 1, 20);
    camera.add(torch);
    scene.add(camera);

    renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(window.innerWidth, window.innerHeight);
    container.appendChild(renderer.domElement);

      buildMaze3D();
      drawMinimap();
      animate();

      window.addEventListener("resize", onWindowResize);
      document.addEventListener("keydown", onKeyDown);
      document.addEventListener("keyup", onKeyUp);
      const startButton = startScreen.querySelector(".laby-btn-start");
      if (startButton) {
        startButton.addEventListener("click", onStart);
      }

      cleanup = () => {
        window.removeEventListener("resize", onWindowResize);
        document.removeEventListener("keydown", onKeyDown);
        document.removeEventListener("keyup", onKeyUp);
        if (startButton) {
          startButton.removeEventListener("click", onStart);
        }
        if (animationId) cancelAnimationFrame(animationId);
        if (renderer) {
          renderer.dispose();
          if (renderer.domElement && renderer.domElement.parentNode) {
            renderer.domElement.parentNode.removeChild(renderer.domElement);
          }
        }
        walls.splice(0, walls.length);
      };
    }

    setup();

    return () => {
      cancelled = true;
      cleanup();
    };
  }, []);

  return (
    <div className="laby-root">
      <div className="laby-canvas-container" ref={canvasRef} />

      <div className="laby-minimap-container" ref={minimapContainerRef}>
        <canvas ref={minimapRef} width={200} height={200} />
      </div>

      <div className="laby-ui-layer">
        <div className="laby-start-screen" ref={startRef}>
          <h1>Labyrinthe 3D</h1>
          <p>Explorez et trouvez la sortie (le pilier vert).</p>
          <p>
            <b>Déplacements :</b> <span className="laby-key">←</span>
            <span className="laby-key">↑</span>
            <span className="laby-key">→</span>
            <span className="laby-key">↓</span>
            <br />
            <b>Regarder :</b> Joystick mobile
            <br />
            <b>Sauter :</b> <span className="laby-key">Espace</span>
          </p>
          <button className="laby-btn-start" type="button">
            COMMENCER
          </button>
        </div>
        <div className="laby-message" ref={messageRef} />
      </div>

      <div className="laby-controls-hint">Flèches pour bouger, joysticks sur mobile</div>
      <div className="gallerie-joysticks">
        <div
          className="gallerie-joystick"
          onPointerDown={handleMoveStart}
          onPointerMove={handleMove}
          onPointerUp={handleMoveEnd}
          onPointerCancel={handleMoveEnd}
        >
          <span className="gallerie-joystick-label">Deplacer</span>
          <div className="gallerie-joystick-stick" />
        </div>
        <div
          className="gallerie-joystick"
          onPointerDown={handleLookStart}
          onPointerMove={handleLook}
          onPointerUp={handleLookEnd}
          onPointerCancel={handleLookEnd}
        >
          <span className="gallerie-joystick-label">Regarder</span>
          <div className="gallerie-joystick-stick" />
        </div>
      </div>
    </div>
  );
}
